import Delete from "@mui/icons-material/Delete";
import { type GridRowSelectionModel } from "@mui/x-data-grid-pro";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import apiRoutes from "@/constants/api-routes";
import messageLevels from "@/constants/message-levels";
import messages from "@/constants/messages";
import pages from "@/constants/pages";
import {
	emptyGridRowSelectionModel,
	resolveGridSelectedRowIds,
} from "@/helpers/dataGridRowSelectionModel";
import useAuthHeader from "@/hooks/useAuthHeader";
import useDatagridSorting from "@/hooks/useDatagridSorting";
import useToastAlert from "@/hooks/useToastAlert";
import { selectOrganizationId } from "@/redux/userSlice";
import CoAppDatagridHeader from "@/components/global/components/datagrid/CoAppDatagridHeader";
import CoAppStandardDataGrid from "@/components/global/components/datagrid/CoAppStandardDataGrid";
import useCoAppDataGridFilters from "@/components/global/components/datagrid/useCoAppDataGridFilters";
import CoAppDestructiveConfirmationModal from "@/components/global/components/modals/CoAppDestructiveConfirmationModal";
import SettingsDataGridSelectionRow, { destructiveIconSx } from "@/components/settings/SettingsDataGridSelectionRow";

import { getCollectionManagementColumns } from "./columns/collection-management-columns";
import type { Collection } from "./CollectionsContext";
import { useCollections } from "./CollectionsContext";
import CreateCollectionModal from "./modals/CreateCollectionModal";

interface Platform {
	id: number;
	name: string;
}
interface PlatformOption {
	active: number;
	platform: Platform;
}

export default function CollectionManagement() {
	const navigate = useNavigate();
	const authHeader = useAuthHeader();
	const organizationId = useSelector(selectOrganizationId);
	const { handleToastAlert } = useToastAlert();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("collections");
	const { collections, loading, createCollection, deleteCollections, isCollectionNameTaken } = useCollections();

	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [collectionsPendingDelete, setCollectionsPendingDelete] = useState<Collection[]>([]);
	const [selected, setSelected] = useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
	const [platforms, setPlatforms] = useState<PlatformOption[]>([]);
	const [platformsLoading, setPlatformsLoading] = useState(false);

	const openEntries = useCallback(
		(collectionId: string) => {
			navigate(pages.collection(collectionId));
		},
		[navigate],
	);

	const requestDeleteCollection = useCallback((collection: Collection) => {
		if (collection.rulesUsingCount > 0) return;
		setCollectionsPendingDelete([collection]);
		setDeleteDialogOpen(true);
	}, []);

	const columns = useMemo(
		() => getCollectionManagementColumns(openEntries, requestDeleteCollection),
		[openEntries, requestDeleteCollection],
	);

	const { gridFilterModel, onFilterModelChange, quickFilterSearchProps, apiRef, visibleRowCount, filterControl, filterButtonEl } =
		useCoAppDataGridFilters(collections, loading);

	const selectedIds = resolveGridSelectedRowIds(selected, collections);
	const selectedCount = selectedIds.length;
	const selectedCollections = useMemo(
		() => collections.filter((c) => selectedIds.some((id) => String(id) === String(c.id))),
		[collections, selectedIds],
	);
	const anySelectedInUse = selectedCollections.some((c) => c.rulesUsingCount > 0);

	useEffect(() => {
		handleInitializeSortModel();
	}, [handleInitializeSortModel]);

	useEffect(() => {
		if (!organizationId) return;
		setPlatformsLoading(true);
		axios
			.get<PlatformOption[]>(apiRoutes.getPlatforms(organizationId), { headers: authHeader })
			.then((response) => {
				setPlatforms(response.data);
			})
			.catch((err) => {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, "Could not load platforms.");
			})
			.finally(() => {
				setPlatformsLoading(false);
			});
	}, [organizationId, authHeader, handleToastAlert]);

	const handleCreate = async (input: { name: string; platformId: number; dataTypeId: number }) => {
		try {
			const created = await createCollection(input);
			setCreateDialogOpen(false);
			navigate(pages.collection(created.id));
		} catch {
			// toast handled in provider
		}
	};

	const openBulkDeleteDialog = () => {
		if (selectedCount === 0 || anySelectedInUse) return;
		setCollectionsPendingDelete(selectedCollections);
		setDeleteDialogOpen(true);
	};

	const confirmDeleteCollections = async () => {
		const deletable = collectionsPendingDelete.filter((c) => c.rulesUsingCount === 0);
		if (deletable.length === 0) return;
		try {
			await deleteCollections(deletable.map((c) => c.id));
			setSelected(emptyGridRowSelectionModel());
			setCollectionsPendingDelete([]);
			setDeleteDialogOpen(false);
		} catch {
			// toast handled in provider
		}
	};

	const pendingDeleteCount = collectionsPendingDelete.length;
	const singlePendingDelete = pendingDeleteCount === 1 ? collectionsPendingDelete[0] : undefined;

	return (
		<>
			<CoAppDatagridHeader
				iconKind="collections"
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={filterControl}
				title="Collections Management"
				resourceType="Collection"
				addOnClickHandler={() => setCreateDialogOpen(true)}
				addIsPresent={true}
				editIsPresent={false}
				deleteIsPresent={false}
			/>
			<CoAppStandardDataGrid
				apiRef={apiRef}
				filterButtonEl={filterButtonEl}
				filterModel={gridFilterModel}
				onFilterModelChange={onFilterModelChange}
				visibleRowCount={visibleRowCount}
				rows={collections}
				columns={columns}
				pinnedColumns={["actions"]}
				allowSelection={true}
				targetResource="collections"
				gridKey="collections"
				selectionModel={selected}
				setSelectionModel={setSelected}
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
				selectionFooter={
					<SettingsDataGridSelectionRow
						resourceNoun="collection"
						visibleCount={visibleRowCount}
						selectedCount={selectedCount}
						loading={loading}
						onClearSelection={() => setSelected(emptyGridRowSelectionModel())}
						actions={
							selectedCount > 0
								? [{
									label: "Delete",
									icon: <Delete sx={destructiveIconSx} />,
									onClick: openBulkDeleteDialog,
									variant: "destructive",
									disabled: anySelectedInUse,
									disabledTooltip: anySelectedInUse ? messages.COLLECTION_BULK_DELETE_IN_USE_TOOLTIP : undefined,
								}]
								: undefined
						}
					/>
				}
			/>
			<CreateCollectionModal
				dialogOpen={createDialogOpen}
				onClose={() => setCreateDialogOpen(false)}
				onCreate={handleCreate}
				isNameTaken={(name) => isCollectionNameTaken(name)}
				platforms={platforms}
				platformsLoading={platformsLoading}
			/>
			{collectionsPendingDelete.length > 0 ? (
				<CoAppDestructiveConfirmationModal
					dialogOpen={deleteDialogOpen}
					dialogTitle={
						pendingDeleteCount > 1
							? "Delete selected collections?"
							: `Delete ${singlePendingDelete?.name ?? "this collection"} collection?`
					}
					dialogMessage={messages.DELETION_CONFIRMATION_MSG(
						pendingDeleteCount > 1 ? "selected collections" : (singlePendingDelete?.name ?? "this collection"),
						"collection",
					)}
					confirmClickHandler={confirmDeleteCollections}
					cancelClickHandler={() => {
						setDeleteDialogOpen(false);
						setCollectionsPendingDelete([]);
					}}
					actionText="Delete"
				/>
			) : null}
		</>
	);
}
